import { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import FetchWithGif from '../../../Reutilizables/FetchWithGif';
import logo from '../../../../img/logo/Logo_FICSULLANA.png';
import { getBase64FromImage } from '../../../../utilities/pdfLogo';

const CONSTANTES = {
    ORIENTACION: 'landscape',
    UNIDAD: 'mm',
    FORMATO: 'a4',
    MARGENES: {
        PREDETERMINADO: 15,
        TABLA: 15
    },
    LOGO: {
        ANCHO: 50,
        ALTO: 48,
        POSICION_Y: 9
    },
    COLORES: {
        ROJO_PRIMARIO: [230, 0, 0],
        NEGRO: [44, 62, 80],
        BLANCO: [255, 255, 255],
        GRIS_100: [150, 150, 150],
        GRIS_60: [108, 122, 137],
        GRIS_CLARO: [230, 230, 230]
    },
    FUENTES: {
        TAMANIOS: {
            TITULO: 20,
            SUBTITULO: 13,
            NORMAL: 10,
            PEQUENIO: 9,
            MUY_PEQUENIO: 8
        }
    },
    TABLA: {
        ENCABEZADOS: [
            '#',
            'ID Préstamo',
            'Cliente',
            'Monto',
            'Tasa %',
            'Cuotas Pagadas',
            'Cuotas Pendientes',
            'Saldo Pendiente',
            'Fecha Venc.',
            'Abonado Por',
            'Teléfono',
            'Dirección'
        ],
        ESTILOS_COLUMNAS: {
            0: { cellWidth: 9 },
            1: { cellWidth: 18 },
            2: { cellWidth: 35 },
            3: { cellWidth: 20 },
            4: { cellWidth: 13 },
            5: { cellWidth: 18 },
            6: { cellWidth: 20 },
            7: { cellWidth: 22 },
            8: { cellWidth: 20 },
            9: { cellWidth: 24 },
            10: { cellWidth: 18 },
            11: { cellWidth: 'auto' }
        }
    }
};

const ReportePrestamosActivos = ({
    loans = [],
    filters = {},
    asesores = [],
    totalRegistros = 0,
    onComplete,
    onPDFGenerated
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [logoBase64Cache, setLogoBase64Cache] = useState(null);
    const hasStartedRef = useRef(false);
    const establecerFuenteDocumento = (doc, estilo, tamanio) => {
        doc.setFont('helvetica', estilo);
        doc.setFontSize(tamanio);
    };

    const establecerColorTexto = (doc, color) => {
        doc.setTextColor(...color);
    };

    const formatearFecha = (fecha) => {
        try {
            return format(new Date(fecha), 'dd/MM/yyyy');
        } catch {
            return format(new Date(), 'dd/MM/yyyy');
        }
    };

    const agregarLogoFallback = (doc, anchoPagina) => {
        const logoAncho = 45;
        const logoAlto = 50;
        const logoX = (anchoPagina - logoAncho) / 2;

        doc.setFillColor(...CONSTANTES.COLORES.BLANCO);
        doc.setDrawColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.setLineWidth(2);
        doc.roundedRect(logoX, 8, logoAncho, logoAlto, 3, 3, 'FD');

        establecerFuenteDocumento(doc, 'bold', 11);
        establecerColorTexto(doc, CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.text('FICS', logoX + logoAncho / 2, 28, { align: 'center' });
        doc.text('ULLANA', logoX + logoAncho / 2, 38, { align: 'center' });
    };

    const agregarLogo = async (doc) => {
        try {
            if (!logoBase64Cache) {
                const logoBase64 = await getBase64FromImage(logo);
                setLogoBase64Cache(logoBase64);
            }

            doc.addImage(
                logoBase64Cache || await getBase64FromImage(logo),
                'PNG',
                CONSTANTES.MARGENES.PREDETERMINADO,
                CONSTANTES.LOGO.POSICION_Y,
                CONSTANTES.LOGO.ANCHO,
                CONSTANTES.LOGO.ALTO
            );
        } catch (error) {
            console.warn('No se pudo cargar el logo, usando fallback');
            const anchoPagina = doc.internal.pageSize.getWidth();
            agregarLogoFallback(doc, anchoPagina);
        }
    };

    const agregarEncabezado = (doc, anchoPagina) => {
        doc.setLineWidth(3);
        doc.setDrawColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.line(0, 0, anchoPagina, 0);

        const textoX = anchoPagina - CONSTANTES.MARGENES.PREDETERMINADO;
        const encabezadoY = 38;
        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.TITULO);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text('REPORTE DE PRÉSTAMOS ACTIVOS', textoX, encabezadoY + 6, { align: 'right' });
    };

    const dibujarSeccion = (doc, titulo, y, anchoPagina, altoPagina) => {
        const altoSeccion = 6;
        if (y + altoSeccion + 5 > altoPagina - 50) {
            doc.addPage();
            y = 20;
        }

        doc.setFillColor(...CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.rect(
            CONSTANTES.MARGENES.PREDETERMINADO,
            y,
            anchoPagina - 2 * CONSTANTES.MARGENES.PREDETERMINADO,
            altoSeccion,
            'F'
        );

        establecerFuenteDocumento(doc, 'bold', CONSTANTES.FUENTES.TAMANIOS.SUBTITULO);
        establecerColorTexto(doc, CONSTANTES.COLORES.BLANCO);
        doc.text(titulo, CONSTANTES.MARGENES.PREDETERMINADO + 5, y + 4.5);

        return y + altoSeccion + 3;
    };

    const obtenerInformacionFiltros = () => {
        const filtrosInfo = [];

        if (filters.search) {
            filtrosInfo.push(`Búsqueda: ${filters.search}`);
        }

        if (filters.asesor_id) {
            const asesor = asesores.find(a => String(a.id) === String(filters.asesor_id));
            filtrosInfo.push(`Asesor: ${asesor?.nombre || filters.asesor_id}`);
        }

        if (filters.abonado_por) {
            filtrosInfo.push(`Abonado por: ${filters.abonado_por}`);
        }

        if (filters.casi_por_terminar) {
            filtrosInfo.push('Filtro: Solo préstamos casi por terminar (1-2 cuotas pendientes)');
        }

        if (filtrosInfo.length === 0) {
            filtrosInfo.push('Sin filtros aplicados - Mostrando todos los préstamos activos');
        }

        return filtrosInfo;
    };

    const agregarSeccionFiltros = (doc, y) => {
        const filtrosInfo = obtenerInformacionFiltros();

        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);

        filtrosInfo.forEach((info) => {
            doc.text(`• ${info}`, CONSTANTES.MARGENES.PREDETERMINADO + 5, y);
            y += 5;
        });

        return y + 3;
    };

    const agregarResumen = (doc, y) => {
        establecerFuenteDocumento(doc, 'bold', 11);
        establecerColorTexto(doc, CONSTANTES.COLORES.ROJO_PRIMARIO);
        doc.text(
            `Total de préstamos activos: ${totalRegistros}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        y += 5;
        establecerFuenteDocumento(doc, 'normal', CONSTANTES.FUENTES.TAMANIOS.NORMAL);
        establecerColorTexto(doc, CONSTANTES.COLORES.NEGRO);
        doc.text(
            `Registros en este reporte: ${loans.length}`,
            CONSTANTES.MARGENES.PREDETERMINADO + 5,
            y
        );

        return y + 8;
    };

    const formatearDatosTabla = () => {
        return loans.map((loan, index) => [
            (index + 1).toString(),
            loan.idPrestamo?.toString() || '-',
            loan.cliente || '-',
            loan.monto_prestamo ? `S/ ${parseFloat(loan.monto_prestamo).toFixed(2)}` : '-',
            loan.tasa_interes ? `${parseFloat(loan.tasa_interes).toFixed(2)}%` : '-',
            loan.cuotas_pagadas?.toString() || '0',
            loan.cuotas_pendientes?.toString() || '0',
            loan.saldo_pendiente ? `S/ ${parseFloat(loan.saldo_pendiente).toFixed(2)}` : 'S/ 0.00',
            loan.fecha_ultima_cuota || '-',
            loan.abonado_por || loan.prestamo?.abonado_por || '-',
            loan.telefono || '-',
            loan.direccion || '-'
        ]);
    };

    const agregarTabla = (doc, y, anchoPagina, altoPagina) => {
        const datos = formatearDatosTabla();
        const fechaActual = formatearFecha(new Date());

        autoTable(doc, {
            head: [CONSTANTES.TABLA.ENCABEZADOS],
            body: datos,
            startY: y,
            margin: {
                left: CONSTANTES.MARGENES.TABLA,
                right: CONSTANTES.MARGENES.TABLA
            },
            tableWidth: 'auto',
            theme: 'striped',
            headStyles: {
                fillColor: CONSTANTES.COLORES.ROJO_PRIMARIO,
                textColor: CONSTANTES.COLORES.BLANCO,
                fontStyle: 'bold',
                fontSize: CONSTANTES.FUENTES.TAMANIOS.PEQUENIO,
                halign: 'center',
                valign: 'middle'
            },
            styles: {
                fontSize: CONSTANTES.FUENTES.TAMANIOS.MUY_PEQUENIO,
                cellPadding: 2,
                valign: 'middle',
                overflow: 'linebreak'
            },
            bodyStyles: {
                textColor: CONSTANTES.COLORES.NEGRO,
                halign: 'center'
            },
            columnStyles: CONSTANTES.TABLA.ESTILOS_COLUMNAS,
            didDrawPage: (data) => {
                const numeroPaginas = doc.internal.getNumberOfPages();
                const paginaActual = doc.internal.getCurrentPageInfo().pageNumber;

                establecerFuenteDocumento(doc, 'italic', CONSTANTES.FUENTES.TAMANIOS.MUY_PEQUENIO);
                establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
                doc.text(
                    `Página ${paginaActual} de ${numeroPaginas}`,
                    anchoPagina / 2,
                    altoPagina - 10,
                    { align: 'center' }
                );
            }
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        establecerFuenteDocumento(doc, 'italic', CONSTANTES.FUENTES.TAMANIOS.PEQUENIO);
        establecerColorTexto(doc, CONSTANTES.COLORES.GRIS_100);
        doc.text(
            `Fecha de emisión: ${fechaActual}`,
            anchoPagina / 2,
            finalY + 3,
            { align: 'center' }
        );
    };

    const generarPDF = async () => {
        if (isGenerating) return;

        setIsGenerating(true);

        try {
            const doc = new jsPDF({
                orientation: CONSTANTES.ORIENTACION,
                unit: CONSTANTES.UNIDAD,
                format: CONSTANTES.FORMATO,
                compress: true
            });

            const anchoPagina = doc.internal.pageSize.getWidth();
            const altoPagina = doc.internal.pageSize.getHeight();
            let y = CONSTANTES.MARGENES.PREDETERMINADO;

            agregarEncabezado(doc, anchoPagina);
            await agregarLogo(doc);
            y += 45;
            y = dibujarSeccion(doc, 'FILTROS APLICADOS', y, anchoPagina, altoPagina);
            y += 3;
            y = agregarSeccionFiltros(doc, y);
            y = agregarResumen(doc, y);
            y = dibujarSeccion(doc, 'LISTADO DE PRÉSTAMOS ACTIVOS', y, anchoPagina, altoPagina);
            agregarTabla(doc, y, anchoPagina, altoPagina);

            const pdfBlob = doc.output('blob');
            const pdfUrl = URL.createObjectURL(pdfBlob);

            if (onPDFGenerated) {
                onPDFGenerated(pdfUrl);
            }

            toast.success('PDF generado correctamente');
            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error al generar PDF:', error);
            toast.error('Error al generar el PDF');

            if (onComplete) {
                onComplete();
            }
        } finally {
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        if (hasStartedRef.current) return;
        hasStartedRef.current = true;
        generarPDF();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return isGenerating ? <FetchWithGif /> : null;
};

export default ReportePrestamosActivos;